import React from 'react';
import { Empty, EllipsisSpan, Select } from '@maxtropy/components';
import styles from './index.module.scss';

interface RankingItem {
  name: string;
  percent: number;
  value?: number;
  barColor?: string; // 单项进度条颜色
}

interface FilterOption {
  label: string;
  value: string;
}

interface ColumnHeaders {
  rank?: string; // 排名列标题
  name?: string; // 名称列标题
  value?: string; // 数值列标题
}

interface RankingCardProps {
  title: string;
  data: RankingItem[];
  barColor?: string; // 全局进度条颜色（默认）
  showValue?: boolean; // 是否显示数值
  showPercent?: boolean; // 是否显示占比
  unit?: string; // 单位文字，如"元"
  valueColor?: string; // 数值的颜色
  valueFormatter?: (value: number) => string; // 自定义数值格式化
  dynamicBarColor?: boolean; // 是否使用每项独立的进度条颜色（百分比颜色也会跟随）
  columnHeaders?: ColumnHeaders; // 列标题行配置
  // 下拉筛选相关
  filterOptions?: FilterOption[]; // 下拉选项
  selectedFilter?: string; // 当前选中值
  onFilterChange?: (value: string) => void; // 下拉变更回调
}

const RankingCard: React.FC<RankingCardProps> = ({
  title,
  data,
  barColor = '#16DD8E',
  showValue = false,
  showPercent = false,
  unit,
  valueColor = '#16DD8E',
  valueFormatter,
  dynamicBarColor = false,
  columnHeaders,
  filterOptions,
  selectedFilter,
  onFilterChange,
}) => {
  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return '#B34043';
      case 2:
        return '#B3552C';
      case 3:
        return '#B3923D';
      default:
        return '#1B9D6C';
    }
  };

  const isEmpty = !data || data.length === 0;

  // 是否显示表头行（筛选 + 单位 + 占比）
  const showHeaderRow = (filterOptions && filterOptions.length > 0) || unit || showPercent;

  // 是否显示列标题行
  const showColumnHeaderRow = columnHeaders && (columnHeaders.rank || columnHeaders.name || columnHeaders.value);

  // 获取当前项的颜色（用于进度条和百分比）
  const getItemColor = (item: RankingItem) => {
    return dynamicBarColor && item.barColor ? item.barColor : barColor;
  };

  return (
    <div className={styles.ranking_card}>
      <div className={styles.card_header}>
        <span className={styles.card_title}>{title}</span>
      </div>
      {/* 篮选、单位和占比表头 - 始终显示 */}
      {showHeaderRow && (
        <div className={styles.header_row}>
          <div className={styles.header_left}>
            {filterOptions && filterOptions.length > 0 && (
              <Select
                value={selectedFilter}
                onChange={onFilterChange}
                options={filterOptions}
                style={{ width: 100 }}
                showSearch
                optionFilterProp="label"
              />
            )}
            {unit && <span className={styles.header_unit}>单位：{unit}</span>}
          </div>
          {showPercent && <span className={styles.header_percent}>占比</span>}
        </div>
      )}
      {isEmpty ? (
        <div className={styles.empty_content}>
          <Empty />
        </div>
      ) : (
        <>
          {/* 列标题行：排名、名称、数值 */}
          {showColumnHeaderRow && (
            <div className={styles.column_header_row}>
              <div className={styles.column_header_rank}>{columnHeaders?.rank || '排名'}</div>
              <div className={styles.column_header_name}>{columnHeaders?.name || '名称'}</div>
              <div className={styles.column_header_value}>{columnHeaders?.value || '数值'}</div>
            </div>
          )}
          <div className={styles.ranking_list}>
            {data.map((item, i) => {
              const itemColor = getItemColor(item);
              return (
                <div key={i} className={styles.ranking_item}>
                  <div className={styles.ranking_left}>
                    <span className={styles.ranking_index} style={{ background: getRankColor(i + 1) }}>
                      {i + 1}
                    </span>
                    <EllipsisSpan value={item.name} />
                  </div>
                  <div className={styles.ranking_right}>
                    <div className={styles.progress_bar}>
                      <div
                        className={styles.progress_fill}
                        style={{
                          width: `${item.percent}%`,
                          background: itemColor,
                        }}
                      />
                    </div>
                    {showValue && item.value !== undefined && (
                      <span
                        className={styles.ranking_value}
                        style={{ color: dynamicBarColor ? itemColor : valueColor }}
                      >
                        {valueFormatter ? valueFormatter(item.value) : `${item.value}`}
                      </span>
                    )}
                    {showPercent && (
                      <span
                        className={styles.ranking_percent_label}
                        style={dynamicBarColor ? { color: itemColor } : undefined}
                      >
                        {item.percent}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default RankingCard;
